import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import PageSkeleton from '../../components/common/page-skeleton';
import ScreenHeader from '../../components/common/screen-header';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import {
  appConfigService,
  stringifyValue,
  type AppConfig,
  type AppConfigHistoryEntry,
} from '../../services/app-config.service';
import { extractErrorMessage } from '../../utils/error-handler';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { AuditInfoCard } from '../../components/common/audit-info-card';
import { useCanUpdate } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import { formatDate } from 'sharedUi/dateUtils';


/** A labelled value, matching the field styling every other view screen uses. */
const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="text-xs text-ink-muted block mb-0.5">{label}</label>
    <div className="text-sm text-ink">{children}</div>
  </div>
);

const AppConfigView = () => {
  const navigate = useAppNavigate();
  const { configKey } = useParams<{ configKey: string }>();
  const [searchParams] = useSearchParams();
  const scopedAppId = searchParams.get('appId') || undefined;
  const canUpdate = useCanUpdate(JOB_NAMES.APP_CONFIG);
  const toast = useRef<Toast>(null);

  const [config, setConfig] = useState<AppConfig | null>(null);
  const [history, setHistory] = useState<AppConfigHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // The key rather than a decoded id — this screen is addressed by configKey.
  useBreadcrumbTitle(config?.configKey);

  useEffect(() => {
    if (!configKey) return;
    let cancelled = false;
    const key = decodeURIComponent(configKey);
    (async () => {
      try {
        const [record, historyPage] = await Promise.all([
          appConfigService.getByKey(key, scopedAppId),
          appConfigService.getHistory(key, { appId: scopedAppId, page: 1, pageSize: 50 }),
        ]);
        if (cancelled) return;
        setConfig(record);
        setHistory(historyPage.data || []);
      } catch (err) {
        if (cancelled) return;
        setError(extractErrorMessage(err, 'Failed to load the configuration'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [configKey, scopedAppId]);

  if (loading) {
    return (
      <PageSkeleton />
    );
  }

  if (error || !config) {
    return (
      <div className="p-4">
        <Message severity="error" text={error || 'Configuration not found'} className="mb-4 w-full" />
        <Button
          label="Back to Application Configuration"
          icon="pi pi-arrow-left"
          severity="secondary"
          outlined
          onClick={() => navigate('/app-config')}
          testId="AppConfigView.Button.BackToList"
        />
      </div>
    );
  }

  const isEncrypted = config.dataType === 'ENCRYPTED';
  const editHref = `/app-config/${encodeURIComponent(config.configKey)}/edit${config.appId ? `?appId=${config.appId}` : ''}`;

  return (
    <PermissionGuard jobName={JOB_NAMES.APP_CONFIG}>
      <div className="p-3" data-testid="SCR-AppConfig-View">
        <Toast ref={toast} />

        {/* Header Section */}
        <ScreenHeader
          icon="pi pi-cog"
          title="Configuration Details"
          onBack={() => navigate('/app-config')}
        />

        {/* Configuration Profile Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-2">
          <div className="flex items-start gap-2.5">
            <div className="w-12 h-12 rounded bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0">
              <i className="pi pi-sliders-h text-indigo-600 dark:text-indigo-400" style={{ fontSize: '1.25rem' }} />
            </div>

            <div>
              <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                <h2 className="text-base font-semibold text-ink font-mono">
                  {config.configKey}
                </h2>
                <Tag value={config.dataType} severity="info" className="text-xs px-1.5 py-0.5" />
                <Tag
                  value={config.isActive ? 'Active' : 'Inactive'}
                  severity={config.isActive ? 'success' : 'danger'}
                  className="text-xs px-1.5 py-0.5"
                />
              </div>
              <p className="text-xs text-ink-muted">{config.description || '-'}</p>
            </div>
          </div>

          {canUpdate && (
            <div className="flex gap-1.5 sm:self-center">
              <Button
                label="Edit"
                icon="pi pi-pencil"
                onClick={() => navigate(editHref)}
                size="small"
                testId="AppConfigView.Button.Edit"
              />
            </div>
          )}
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
          {/* Basic Information */}
          <div className="ds-card p-3">
            <h2 className="text-sm font-semibold text-ink mb-2">Basic Information</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
              <Field label="Configuration Key">
                <span className="font-mono">{config.configKey}</span>
              </Field>
              <Field label="Data Type">
                <span className="font-mono">{config.dataType}</span>
              </Field>
              <Field label="Status">{config.isActive ? 'Active' : 'Inactive'}</Field>
              <Field label="Last Updated">
                {config.updatedAt ? formatDate(config.updatedAt) : '-'}
              </Field>
            </div>
          </div>

          {/* Description */}
          <div className="ds-card p-3">
            <h2 className="text-sm font-semibold text-ink mb-2">Description</h2>
            <div className="grid grid-cols-1 gap-y-2">
              <Field label="Description">{config.description || '-'}</Field>
            </div>
          </div>

          {/* Value */}
          <div className="ds-card lg:col-span-2 p-3">
            <h2 className="text-sm font-semibold text-ink mb-2">Value</h2>
            <div className="grid grid-cols-1 gap-y-2">
              <div>
                <label className="text-xs text-ink-muted block mb-0.5">Stored Value</label>
                <pre className="overflow-x-auto rounded bg-canvas dark:bg-gray-900 p-3 font-mono text-xs text-ink">
                  {isEncrypted
                    ? '•••••••• (encrypted, never returned)'
                    : stringifyValue(config.dataType, config.value) || '-'}
                </pre>
              </div>

              {!config.isActive && (
                <Message
                  severity="warn"
                  className="w-full justify-content-start"
                  text="Deactivated, so it resolves to nothing at runtime. Anything reading this key reports it as unavailable."
                />
              )}
            </div>
          </div>

          {/* Change history */}
          <div className="ds-card lg:col-span-2 p-3">
            <h2 className="text-sm font-semibold text-ink mb-2">Change History</h2>
            <p className="text-xs text-ink-muted mb-2">
              {/* Append-only, and worth stating: this is the record an audit relies on. */}
              Newest first. Every change is recorded and nothing here can be edited or removed.
            </p>

            {history.length === 0 ? (
              <p className="text-sm text-ink-muted">No history recorded.</p>
            ) : (
              <ol className="space-y-2">
                {history.map((entry) => (
                  <li
                    key={entry._id}
                    className="rounded border border-line p-2.5"
                  >
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Tag
                        value={entry.action}
                        severity={entry.action === 'CREATE' ? 'success' : 'info'}
                        className="text-xs px-1.5 py-0.5"
                      />
                      <span className="text-xs text-ink-muted">
                        {entry.changedAt ? formatDate(entry.changedAt) : '-'}
                      </span>
                      {entry.changedFields?.length > 0 && (
                        <span className="font-mono text-xs text-ink-muted">
                          {entry.changedFields.join(', ')}
                        </span>
                      )}
                    </div>
                    {/* WHAT changed, never the values. A credential's history row
                        would otherwise put the setting's contents on a screen the
                        record itself keeps out of reach — and for an ordinary key
                        the before/after pair was noise. The full record remains in
                        `app_config_history` for an audit that needs it. */}
                  </li>
                ))}
              </ol>
            )}
          </div>

          <AuditInfoCard
            className="lg:col-span-2"
            createdAt={config.createdAt}
            updatedAt={config.updatedAt}
            createdBy={config.createdBy}
            updatedBy={config.updatedBy}
          />
        </div>
      </div>
    </PermissionGuard>
  );
};

export default AppConfigView;
